import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { Container, Stack } from "@mantine/core";
import { type JSX, useMemo } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { usePlatformGuideManager } from "@/guides";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { ConfettiAnimation } from "@/sales-tools/reactions/Confetti";
import { isVoiceTask, useActiveTask, useAgentStore, useCallbackState, usePartner, useSession } from "@/sdk";
import { DeprecatedSessionSummary, SessionSummary } from "@/session-summary";
import { userCache } from "@/shared-utils/auth";
import { createFallbackRender } from "@/shared-utils/fallbackRender";
import { PlatformTimeline } from "@/timeline";
import styles from "./workspace.module.css";

const logger = getLogger({ module: "WorkspaceBody" });

const PlatformTimelineHandled = withErrorBoundary(PlatformTimeline, { fallbackRender: createFallbackRender(logger) });
const SessionSummaryHandled = withErrorBoundary(SessionSummary, { fallbackRender: createFallbackRender(logger) });
const DeprecatedSessionSummaryHandled = withErrorBoundary(DeprecatedSessionSummary, {
    fallbackRender: createFallbackRender(logger),
});

export function WorkspaceBody(): JSX.Element {
    // Hooks
    const session = useSession();
    const partner = usePartner();
    const callbackState = useCallbackState();
    const activeTask = useActiveTask();
    const activeVoiceTask = useMemo(() => {
        return isVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);
    const { activity: currentActivity } = useAgentStore((state) => state);
    const { simplifiedSummary, callControlsEnabled } = usePartnerFeatureConfigured();
    const {
        features: { customFlowSystemId, isUpdatedEndSessionFlowEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { solveGuidedFlows, wrappingAgentActivity, gaiaPayloadProps } = usePartnerConfig();

    const sessionId = session.id;
    const callSid = activeVoiceTask?.agentCallId;
    const { employeeId } = userCache;
    const isCustomerSession = session.kind === "with-customer";
    const globalConfig = {
        customFlowSystemId: customFlowSystemId ?? "",
        widgetId: solveGuidedFlows?.widgetId ?? "",
    };

    const { areGuidesAvailable, guide, guideToolboxCommand } = usePlatformGuideManager({
        activeVoiceTask,
        callSid,
        employeeId: employeeId ?? "",
        globalConfig,
        isCustomerSession,
        logger,
        partner,
        sessionId,
        gaiaPayloadProps,
    });

    // State
    const hasPendingCallback = callbackState?.callbackType === "CallbackNow";
    const displaySummary =
        isCustomerSession &&
        (currentActivity === wrappingAgentActivity || activeTask?.status === "wrapping") &&
        !hasPendingCallback;

    return (
        <>
            <Stack className={styles.workspaceBody} gap="sm" pt={!callControlsEnabled ? "0" : undefined}>
                <Container className={styles.animationContainer} key={sessionId}>
                    <ConfettiAnimation />
                </Container>

                {areGuidesAvailable ? guide : null}

                <PlatformTimelineHandled guide={guideToolboxCommand} />
            </Stack>

            {displaySummary &&
                (isUpdatedEndSessionFlowEnabled && !simplifiedSummary ? (
                    <SessionSummaryHandled />
                ) : (
                    <DeprecatedSessionSummaryHandled />
                ))}
        </>
    );
}
